/* Tema Tarot – comportamiento interactivo (sin librerías externas) */

const designMode = Boolean(window.Shopify && window.Shopify.designMode);
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function onceVisible(element, callback, options = { threshold: 0.25 }) {
  if (designMode || !('IntersectionObserver' in window)) {
    callback();
    return null;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      callback();
    });
  }, options);
  observer.observe(element);
  return observer;
}

/* Aparición suave de bloques con [data-reveal] */
function initReveal(root = document) {
  const items = root.querySelectorAll('[data-reveal]:not(.is-revealed)');
  if (designMode || reducedMotion || !('IntersectionObserver' in window)) {
    items.forEach((item) => item.classList.add('is-revealed'));
    return;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  items.forEach((item) => observer.observe(item));
}

document.addEventListener('DOMContentLoaded', () => initReveal());
document.addEventListener('shopify:section:load', (event) => initReveal(event.target));

/* Carrusel genérico con scroll-snap: flechas, puntos, barra de progreso y autoplay */
class TtSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    if (!this.track) return;
    this.prevButton = this.querySelector('[data-slider-prev]');
    this.nextButton = this.querySelector('[data-slider-next]');
    this.dots = this.querySelector('[data-slider-dots]');
    this.thumb = this.querySelector('[data-slider-thumb]');
    this.loop = this.hasAttribute('data-loop');

    this.prevButton?.addEventListener('click', () => this.go(-1));
    this.nextButton?.addEventListener('click', () => this.go(1));
    this.track.addEventListener('scroll', () => window.requestAnimationFrame(() => this.update()), { passive: true });
    this.resizeObserver = new ResizeObserver(() => {
      this.buildDots();
      this.update();
    });
    this.resizeObserver.observe(this.track);

    const autoplay = parseInt(this.dataset.autoplay || '0', 10);
    if (autoplay > 0 && !reducedMotion && !designMode) {
      this.addEventListener('mouseenter', () => (this.paused = true));
      this.addEventListener('mouseleave', () => (this.paused = false));
      this.addEventListener('focusin', () => (this.paused = true));
      this.addEventListener('focusout', () => (this.paused = false));
      this.timer = window.setInterval(() => {
        if (!this.paused && document.visibilityState === 'visible') this.go(1, true);
      }, autoplay);
    }
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
    this.resizeObserver?.disconnect();
  }

  get slides() {
    return Array.from(this.track.children).filter((slide) => slide.offsetParent !== null);
  }

  get step() {
    const first = this.slides[0];
    if (!first) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  get maxScroll() {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  }

  get pageCount() {
    return Math.max(1, Math.round(this.maxScroll / this.step) + 1);
  }

  get index() {
    return Math.round(this.track.scrollLeft / this.step);
  }

  go(direction, fromAutoplay = false) {
    const left = this.track.scrollLeft;
    if ((this.loop || fromAutoplay) && direction > 0 && left >= this.maxScroll - 2) {
      this.track.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }
    if (this.loop && direction < 0 && left <= 2) {
      this.track.scrollTo({ left: this.maxScroll, behavior: 'smooth' });
      return;
    }
    this.track.scrollBy({ left: direction * this.step, behavior: 'smooth' });
  }

  goTo(index) {
    this.track.scrollTo({ left: index * this.step, behavior: 'smooth' });
  }

  buildDots() {
    if (!this.dots) return;
    const count = this.pageCount;
    if (this.dots.childElementCount === count) return;
    this.dots.innerHTML = '';
    for (let i = 0; i < count; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'slider-dot';
      dot.setAttribute('aria-label', `${i + 1} / ${count}`);
      dot.addEventListener('click', () => this.goTo(i));
      this.dots.appendChild(dot);
    }
    this.dots.hidden = count < 2;
  }

  update() {
    const index = Math.min(this.index, this.pageCount - 1);
    if (this.dots) {
      Array.from(this.dots.children).forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
    }
    if (this.thumb) {
      const ratio = this.track.clientWidth / this.track.scrollWidth;
      const progress = this.maxScroll ? this.track.scrollLeft / this.maxScroll : 0;
      this.thumb.style.width = `${Math.min(100, ratio * 100)}%`;
      this.thumb.style.transform = `translateX(${progress * (1 / ratio - 1) * 100}%)`;
    }
    if (!this.loop) {
      if (this.prevButton) this.prevButton.disabled = this.track.scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= this.maxScroll - 2;
    }
  }
}
customElements.define('tt-slider', TtSlider);

/* Galería de producto con miniaturas sincronizadas */
class TtGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.strip = this.querySelector('[data-gallery-strip]');
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    if (!this.track) return;
    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goTo(i)));
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.goTo(this.index + 1));
    this.track.addEventListener('scroll', () => window.requestAnimationFrame(() => this.sync()), { passive: true });
  }

  get count() {
    return this.track.children.length;
  }

  get index() {
    return Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
  }

  goTo(index) {
    const target = (index + this.count) % this.count;
    this.track.scrollTo({ left: target * this.track.clientWidth, behavior: 'smooth' });
  }

  sync() {
    const index = this.index;
    if (index === this.current) return;
    this.current = index;
    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
    const active = this.thumbs[index];
    if (active && this.strip) {
      const left = active.offsetLeft - (this.strip.clientWidth - active.clientWidth) / 2;
      this.strip.scrollTo({ left, behavior: 'smooth' });
    }
  }
}
customElements.define('tt-gallery', TtGallery);

/* Círculos de porcentaje que se llenan y números que cuentan al aparecer */
class TtRings extends HTMLElement {
  connectedCallback() {
    this.observer = onceVisible(this, () => this.play());
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  play() {
    this.querySelectorAll('[data-ring]').forEach((ring) => {
      const value = Math.max(0, Math.min(100, parseFloat(ring.dataset.value) || 0));
      const fill = ring.querySelector('[data-ring-fill]');
      const number = ring.querySelector('[data-ring-number]');
      window.requestAnimationFrame(() => {
        if (fill) fill.style.strokeDasharray = `${value} 100`;
      });
      if (!number) return;
      const suffix = number.dataset.suffix || '';
      if (reducedMotion || designMode) {
        number.textContent = `${value}${suffix}`;
        return;
      }
      const start = performance.now();
      const duration = 1500;
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 4);
        number.textContent = `${Math.round(value * eased)}${suffix}`;
        if (t < 1) window.requestAnimationFrame(tick);
      };
      window.requestAnimationFrame(tick);
    });
  }
}
customElements.define('tt-rings', TtRings);

/* Cuenta regresiva hasta la hora de corte (HH:MM) */
class TtCountdown extends HTMLElement {
  connectedCallback() {
    this.update();
    this.timer = window.setInterval(() => this.update(), 20000);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  update() {
    const hour = parseInt(this.dataset.hour || '23', 10);
    const minute = parseInt(this.dataset.minute || '59', 10);
    const now = new Date();
    const target = new Date(now);
    target.setHours(hour, minute, 0, 0);
    if (target <= now) target.setDate(target.getDate() + 1);
    const minutes = Math.floor((target - now) / 60000);
    const hh = String(Math.floor(minutes / 60)).padStart(2, '0');
    const mm = String(minutes % 60).padStart(2, '0');
    this.textContent = `${hh}:${mm}`;
  }
}
customElements.define('tt-countdown', TtCountdown);

/* Acordeón animado sobre <details> */
class TtAccordion extends HTMLElement {
  connectedCallback() {
    this.exclusive = this.hasAttribute('data-exclusive');
    this.items = Array.from(this.querySelectorAll(':scope details'));
    this.items.forEach((details) => {
      details.classList.toggle('is-open', details.open);
      const summary = details.querySelector('summary');
      summary?.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggle(details, !details.classList.contains('is-open'));
      });
    });
  }

  toggle(details, open) {
    const content = details.querySelector('[data-accordion-content]');
    if (open && this.exclusive) {
      this.items.forEach((other) => {
        if (other !== details && other.classList.contains('is-open')) this.toggle(other, false);
      });
    }
    details.classList.toggle('is-open', open);
    details.querySelector('summary')?.setAttribute('aria-expanded', String(open));
    if (!content || reducedMotion) {
      details.open = open;
      return;
    }
    this.animation?.cancel();
    if (open) {
      details.open = true;
      const height = content.scrollHeight;
      content.animate(
        [
          { height: '0px', opacity: 0 },
          { height: `${height}px`, opacity: 1 },
        ],
        { duration: 320, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
      );
    } else {
      const height = content.scrollHeight;
      const animation = content.animate(
        [
          { height: `${height}px`, opacity: 1 },
          { height: '0px', opacity: 0 },
        ],
        { duration: 260, easing: 'ease' }
      );
      animation.onfinish = () => {
        if (!details.classList.contains('is-open')) details.open = false;
      };
    }
  }
}
customElements.define('tt-accordion', TtAccordion);

/* Video de cliente: reproduce/pausa al tocar y pausa los demás */
class TtVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.video.removeAttribute('controls');
    this.video.playsInline = true;
    this.addEventListener('click', () => this.toggle());
    this.video.addEventListener('ended', () => this.classList.remove('is-playing'));
  }

  toggle() {
    if (this.video.paused) {
      document.querySelectorAll('tt-video.is-playing').forEach((other) => {
        if (other !== this) other.pause();
      });
      this.video.play();
      this.classList.add('is-playing');
    } else {
      this.pause();
    }
  }

  pause() {
    this.video?.pause();
    this.classList.remove('is-playing');
  }
}
customElements.define('tt-video', TtVideo);

/* Encabezado: sombra al desplazarse y menú móvil */
class TtHeader extends HTMLElement {
  connectedCallback() {
    this.onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 10);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
    this.drawer = this.querySelector('[data-menu-drawer]');
    this.querySelectorAll('[data-menu-open]').forEach((button) => button.addEventListener('click', () => this.openMenu()));
    this.querySelectorAll('[data-menu-close]').forEach((button) => button.addEventListener('click', () => this.closeMenu()));
    this.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') this.closeMenu();
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }

  openMenu() {
    if (!this.drawer) return;
    this.drawer.classList.add('is-open');
    document.documentElement.classList.add('scroll-locked');
    this.drawer.querySelector('a, button')?.focus();
  }

  closeMenu() {
    if (!this.drawer?.classList.contains('is-open')) return;
    this.drawer.classList.remove('is-open');
    document.documentElement.classList.remove('scroll-locked');
  }
}
customElements.define('tt-header', TtHeader);

/* Producto: tarjetas de variantes, suscripción y panel lateral de preguntas */
class TtProduct extends HTMLElement {
  connectedCallback() {
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.drawer = this.querySelector('[data-drawer]');
    this.form = this.querySelector('[data-questions-form]');
    this.variantInput = this.querySelector('[data-variant-input]');
    this.planInput = this.querySelector('[data-plan-input]');
    this.subscription = this.querySelector('[data-subscription]');
    this.offers = Array.from(this.querySelectorAll('[data-offer]'));

    this.offers.forEach((offer) => {
      offer.querySelector('input')?.addEventListener('change', () => this.selectOffer(offer));
    });
    this.subscription?.addEventListener('change', () => this.refresh());

    this.querySelectorAll('[data-open-drawer]').forEach((button) =>
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.openDrawer();
      })
    );
    this.querySelectorAll('[data-close-drawer]').forEach((button) => button.addEventListener('click', () => this.closeDrawer()));
    this.drawer?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeDrawer();
      if (event.key === 'Tab') this.trapFocus(event);
    });

    if (this.form) {
      // Con JavaScript validamos nosotros (mensajes en español); sin JS rige "required" del navegador
      this.form.noValidate = true;
      this.form.addEventListener('input', () => this.validate(false));
      this.form.addEventListener('change', () => this.validate(false));
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.validate(false);
    }

    const selected = this.offers.find((offer) => offer.querySelector('input')?.checked) || this.offers[0];
    if (selected) this.selectOffer(selected);

    if (designMode) {
      document.addEventListener('shopify:block:select', (event) => {
        if (this.contains(event.target) && event.target.closest('[data-drawer]')) this.openDrawer();
      });
      document.addEventListener('shopify:block:deselect', (event) => {
        if (this.contains(event.target) && event.target.closest('[data-drawer]')) this.closeDrawer();
      });
    }
  }

  formatMoney(cents) {
    const value = (cents / 100).toFixed(2);
    const [whole, decimals] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const withDots = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withCommas;
        case 'amount_with_comma_separator':
          return `${withDots},${decimals}`;
        case 'amount_no_decimals_with_comma_separator':
          return withDots;
        case 'amount_with_apostrophe_separator':
          return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, "'")}.${decimals}`;
        default:
          return `${withCommas}.${decimals}`;
      }
    });
  }

  selectOffer(offer) {
    this.offers.forEach((item) => item.classList.toggle('is-selected', item === offer));
    const input = offer.querySelector('input');
    if (input) input.checked = true;
    this.currentOffer = offer;
    this.refresh();
  }

  refresh() {
    const offer = this.currentOffer;
    if (!offer) return;
    const data = offer.dataset;
    const subscribed = Boolean(this.subscription?.checked && data.planId);
    const price = parseInt(subscribed ? data.planPrice : data.price, 10);
    const compare = parseInt(data.compare || '0', 10);
    const reference = compare > price ? compare : subscribed ? parseInt(data.price, 10) : 0;

    if (this.variantInput) this.variantInput.value = data.variantId;
    if (this.planInput) {
      this.planInput.value = subscribed ? data.planId : '';
      this.planInput.disabled = !subscribed;
    }
    this.subscription?.closest('[data-subscription-box]')?.classList.toggle('is-checked', Boolean(this.subscription?.checked));

    this.querySelectorAll('[data-price-display]').forEach((el) => (el.textContent = this.formatMoney(price)));
    this.querySelectorAll('[data-compare-display]').forEach((el) => {
      el.textContent = reference > price ? this.formatMoney(reference) : '';
      el.hidden = !(reference > price);
    });
    this.querySelectorAll('[data-save-badge]').forEach((el) => {
      const percent = reference > price ? Math.round(((reference - price) / reference) * 100) : 0;
      el.hidden = percent <= 0;
      const label = el.dataset.label || 'Ahorrá [percent]%';
      el.textContent = label.replace('[percent]', percent);
    });
    this.querySelectorAll('[data-drawer-variant]').forEach((el) => (el.textContent = data.title || ''));
  }

  openDrawer() {
    if (!this.drawer) return;
    this.lastFocus = document.activeElement;
    this.drawer.hidden = false;
    window.requestAnimationFrame(() => {
      this.drawer.classList.add('is-open');
      document.documentElement.classList.add('scroll-locked');
      this.drawer.querySelector('[data-drawer-panel]')?.focus();
    });
  }

  closeDrawer() {
    if (!this.drawer?.classList.contains('is-open')) return;
    this.drawer.classList.remove('is-open');
    document.documentElement.classList.remove('scroll-locked');
    window.setTimeout(() => {
      if (!this.drawer.classList.contains('is-open')) this.drawer.hidden = true;
    }, 300);
    this.lastFocus?.focus();
  }

  trapFocus(event) {
    const focusables = Array.from(
      this.drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => el.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* Une día, mes y año en una sola propiedad legible */
  syncBirthDate() {
    const day = this.form.querySelector('[data-dob-day]')?.value || '';
    const month = this.form.querySelector('[data-dob-month]')?.value || '';
    const year = this.form.querySelector('[data-dob-year]')?.value.trim() || '';
    const target = this.form.querySelector('[data-dob-value]');
    if (!target) return true;
    target.value = day && month && year ? `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}` : '';
    if (!day || !month || !/^\d{4}$/.test(year)) return false;
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    const now = new Date();
    return date.getDate() === Number(day) && date <= now && Number(year) > now.getFullYear() - 120;
  }

  validate(showErrors) {
    let firstInvalid = null;
    const dobValid = this.syncBirthDate();
    const dobGroup = this.form.querySelector('[data-dob-group]');
    if (dobGroup) {
      dobGroup.classList.toggle('has-error', showErrors && !dobValid);
      if (!dobValid && !firstInvalid) firstInvalid = dobGroup.querySelector('select, input');
    }
    this.form.querySelectorAll('[data-required]').forEach((field) => {
      const valid = field.type === 'checkbox' ? field.checked : field.value.trim().length > 0;
      const wrapper = field.closest('[data-field]') || field;
      wrapper.classList.toggle('has-error', showErrors && !valid);
      if (!valid && !firstInvalid) firstInvalid = field;
    });
    const valid = !firstInvalid;
    const submit = this.form.querySelector('[data-questions-submit]');
    submit?.setAttribute('aria-disabled', String(!valid));

    // Aviso junto al botón cuando faltan las casillas obligatorias
    const hint = this.form.querySelector('[data-consent-hint]');
    if (hint) {
      const missingConsent = Array.from(this.form.querySelectorAll('[data-consent][data-required]')).some((box) => !box.checked);
      if (showErrors) hint.hidden = !missingConsent;
      else if (!missingConsent) hint.hidden = true;
    }
    if (showErrors && firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstInvalid.focus({ preventScroll: true });
    }
    return valid;
  }

  async submit(event) {
    event.preventDefault();
    if (!this.validate(true)) return;
    const submit = this.form.querySelector('[data-questions-submit]');
    const error = this.form.querySelector('[data-questions-error]');
    submit?.classList.add('is-loading');
    if (error) error.hidden = true;

    const properties = {};
    new FormData(this.form).forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && String(value).trim() !== '') properties[match[1]] = String(value).trim();
    });

    // Cada aceptación queda en el pedido con fecha, hora y zona horaria del cliente (prueba ante contracargos)
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const offset = -now.getTimezoneOffset();
    const zone = `UTC${offset >= 0 ? '+' : '-'}${pad(Math.floor(Math.abs(offset) / 60))}:${pad(Math.abs(offset) % 60)}`;
    const stamp = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())} (${zone})`;
    this.form.querySelectorAll('[data-consent]:checked').forEach((box) => {
      const match = box.name.match(/^properties\[(.+)\]$/);
      if (match) properties[match[1]] = `Sí – ${stamp}`;
    });

    const item = { id: Number(this.variantInput.value), quantity: 1, properties };
    if (this.planInput && !this.planInput.disabled && this.planInput.value) item.selling_plan = Number(this.planInput.value);
    const items = [item];

    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || data.message || 'Error');
      }
      const afterAdd = document.body.dataset.afterAdd;
      const cartDrawer = document.querySelector('tt-cart-drawer');
      if (afterAdd === 'drawer' && cartDrawer) {
        // Se cierra el formulario y se abre el carrito lateral con las opciones express
        submit?.classList.remove('is-loading');
        this.form.reset();
        this.validate(false);
        this.closeDrawer();
        cartDrawer.refresh({ open: true });
        return;
      }
      window.location.href = afterAdd === 'checkout' ? `${root}checkout` : document.body.dataset.cartUrl || `${root}cart`;
    } catch (err) {
      submit?.classList.remove('is-loading');
      if (error) {
        error.textContent = `${error.dataset.prefix || ''} ${err.message}`.trim();
        error.hidden = false;
      }
    }
  }
}
customElements.define('tt-product', TtProduct);

/* Carrito: lógica compartida entre el carrito lateral y la página del carrito.
   - Las entregas express son excluyentes y van una sola vez por pedido.
   - Si no queda ninguna lectura, el express se quita solo. */
const cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

async function cartRequest(path, body) {
  const response = await fetch(`${cartRoot}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error('cart');
  return response.json();
}

async function getCart() {
  const response = await fetch(`${cartRoot}cart.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

/* Devuelve las cantidades a corregir (o null si el carrito ya cumple las reglas) */
function expressFixes(cart, expressIds) {
  const expressItems = cart.items.filter((item) => expressIds.includes(item.variant_id));
  const hasReading = cart.items.some((item) => !expressIds.includes(item.variant_id));
  if (!expressItems.length) return null;
  const updates = {};
  if (!hasReading) {
    expressItems.forEach((item) => (updates[item.variant_id] = 0));
    return updates;
  }
  const total = expressItems.reduce((sum, item) => sum + item.quantity, 0);
  if (total <= 1) return null;
  const [keep, ...rest] = expressItems;
  updates[keep.variant_id] = 1;
  rest.forEach((item) => (updates[item.variant_id] = 0));
  return updates;
}

function updateCartCount(count) {
  document.querySelectorAll('.header__cart').forEach((link) => {
    let bubble = link.querySelector('.header__cart-count');
    if (count > 0) {
      if (!bubble) {
        bubble = document.createElement('span');
        bubble.className = 'header__cart-count';
        link.appendChild(bubble);
      }
      bubble.textContent = count;
    } else {
      bubble?.remove();
    }
    link.setAttribute('aria-label', `Carrito (${count})`);
  });
}

class TtCartBase extends HTMLElement {
  get body() {
    return this.querySelector('[data-cart-body]');
  }

  get expressIds() {
    return (this.body?.dataset.expressVariants || '')
      .split(',')
      .map((id) => Number(id))
      .filter(Boolean);
  }

  bindCartEvents() {
    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-remove-key]');
      if (!remove) return;
      event.preventDefault();
      this.run(() => cartRequest('cart/change.js', { id: remove.dataset.removeKey, quantity: 0 }));
    });
    this.addEventListener('change', (event) => {
      const toggle = event.target.closest('[data-express-toggle]');
      if (!toggle) return;
      const id = Number(toggle.value);
      const updates = {};
      if (toggle.checked) {
        // Una sola opción express: al activar una se apaga la otra
        this.expressIds.forEach((other) => (updates[other] = other === id ? 1 : 0));
      } else {
        updates[id] = 0;
      }
      this.run(() => cartRequest('cart/update.js', { updates }));
    });
  }

  async run(action) {
    this.body?.classList.add('is-busy');
    try {
      await action();
      await this.enforce();
    } catch (error) {
      // Si algo falla se vuelve a mostrar el carrito real
    }
    await this.render();
  }

  /* Corrige el carrito si quedó un express sin lectura o más de un express */
  async enforce() {
    const ids = this.expressIds;
    if (!ids.length) return false;
    const cart = await getCart();
    const fixes = expressFixes(cart, ids);
    if (!fixes) return false;
    await cartRequest('cart/update.js', { updates: fixes });
    return true;
  }
}

class TtCartDrawer extends TtCartBase {
  connectedCallback() {
    this.panel = this.querySelector('.cart-drawer__panel');
    this.bindCartEvents();
    this.querySelectorAll('[data-cart-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
    // El ícono del carrito abre el carrito lateral (salvo en la página del carrito)
    if (document.body.classList.contains('template-cart')) return;
    document.addEventListener('click', (event) => {
      const link = event.target.closest('.header__cart');
      if (!link) return;
      event.preventDefault();
      this.refresh({ open: true });
    });
  }

  async render() {
    const response = await fetch(`${cartRoot}?sections=cart-drawer`);
    const data = await response.json();
    const html = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
    const fresh = html.querySelector('[data-cart-drawer-content]');
    const content = this.querySelector('[data-cart-drawer-content]');
    if (fresh && content) content.innerHTML = fresh.innerHTML;
    updateCartCount(Number(this.body?.dataset.itemCount || 0));
  }

  async refresh({ open = false } = {}) {
    if (open) this.open();
    this.body?.classList.add('is-busy');
    await this.enforce().catch(() => {});
    await this.render();
  }

  open() {
    this.hidden = false;
    window.requestAnimationFrame(() => {
      this.classList.add('is-open');
      document.documentElement.classList.add('scroll-locked');
      this.panel?.focus();
    });
  }

  close() {
    this.classList.remove('is-open');
    document.documentElement.classList.remove('scroll-locked');
    window.setTimeout(() => {
      if (!this.classList.contains('is-open')) this.hidden = true;
    }, 300);
  }
}
customElements.define('tt-cart-drawer', TtCartDrawer);

class TtCartPage extends TtCartBase {
  connectedCallback() {
    this.bindCartEvents();
    // Si el carrito llegó con un express que no corresponde, se corrige y se recarga
    this.enforce()
      .then((changed) => changed && window.location.reload())
      .catch(() => {});
  }

  async render() {
    window.location.reload();
  }
}
customElements.define('tt-cart-page', TtCartPage);
